<div class="licenses layout-spacing" data-testid="licenses">
    <!-- The to be projected content into the subnavbar -->
    <ng-template #subNavBarContent>
        @if (!isDealer()) {
            <ntv-button
                color="custom"
                customColor="#8DCB2C"
                size="xl"
                rounded="md"
                (click)="onAddLicense()">
                <div class="main-btn text-white">
                    <span>Generate License</span>
                    <svg width="14" height="14">
                        <use [attr.href]="spritePath + '#' + icons['licenses']"></use>
                    </svg>
                </div>
            </ntv-button>
        }
    </ng-template>

    <div class="licenses__stats" data-testid="licenses-stats">
        @if (isStatsLoading()) {
            @for (skeleton of [1, 2, 3]; track skeleton) {
                <app-stats-card [isLoading]="true" width="100%" />
            }
        } @else {
            @for (card of statsData(); track card.title + $index) {
                <app-stats-card [statsData]="card" [hasTotal]="true" width="100%" />
            }
        }
    </div>

    <div class="licenses__content" data-testid="licenses-content">
        <app-table-toolbar
            [placeholder]="'Search by License Key, or Alias'"
            [buttons]="toolbarButtons()"
            (actionClick)="onToolbarAction($event)"
            (searchValueChange)="onSearch($event)">
            @if (!isHiddenForNow()) {
                <ng-container leftActions>
                    <app-filter-dropdown
                        type="grouped"
                        [groupedOptions]="filterTreeOptions()"
                        (treeCheckedChange)="onTreeCheckedChange($event)"
                        (treeExpandedChange)="onTreeExpandToggle($event)">
                    </app-filter-dropdown>

                    <ntv-dropdown
                        [config]="timezoneConfig()"
                        (selectionChange)="onTimezoneChange($event)"></ntv-dropdown>

                    <div>
                        @defer (on timer(0)) {
                            <ntv-date-range-picker
                                variant="single"
                                (dates)="onDatesChange($event)"></ntv-date-range-picker>
                        } @placeholder {
                            <div
                                class="h-10 w-32 bg-slate-100 animate-pulse rounded-md"
                                data-testid="h-10"></div>
                        }
                    </div>
                </ng-container>
            }
        </app-table-toolbar>

        <div
            class="licenses__content-table main-table-container"
            data-testid="licenses-content-table">
            <app-table
                [columns]="licenseColumns"
                [data]="filteredLicenseData()"
                [totalDataLength]="totalCount()"
                [loading]="isLoading()"
                [loadingOverlay]="isShowMoreLoading()"
                [tableTitle]="'Licenses'"
                [tableHeight]="'auto'"
                [pageSize]="limit()"
                [hasCheckBox]="false"
                (actionClick)="onActionClick($event)"
                (cellClick)="onCellClick($event)"
                (selectionChange)="onSelectionChange($event)"
                (showMoreRequested)="onShowMore()"
                (showLessRequested)="onShowLess()">
                <ng-template ntvTableCell="anydesk" let-row="row">
                    <div class="anydesk-cell">
                        @if (row.anydesk?.length) {
                            @for (item of row.anydesk; track item.label) {
                                <div class="anydesk-item">
                                    <span class="anydesk-item__label">{{ item.label }}:</span>
                                    <span class="anydesk-item__value">{{
                                        item.value ?? '--'
                                    }}</span>
                                    @if (item.value) {
                                        <button
                                            class="anydesk-item__copy"
                                            [appCopy]="item.value"
                                            title="Copy">
                                            <svg width="12" height="12">
                                                <use
                                                    [attr.href]="
                                                        spritePath + '#' + icons['copy']
                                                    "></use>
                                            </svg>
                                        </button>
                                    }
                                </div>
                            }
                        } @else {
                            <span class="text-slate-400">--</span>
                        }
                    </div>
                </ng-template>

                <ng-template ntvTableCell="tags" let-row="row">
                    <div class="tags-container" data-testid="tags-container">
                        @for (tag of row.tags; track tag + $index) {
                            <span class="tag-badge">{{ tag }}</span>
                        }
                    </div>
                </ng-template>
            </app-table>
        </div>
    </div>
</div>

<!-- Generate License Modal -->
@if (!isDealer() && showGenerateModal()) {
    <app-generate-license-modal
        [isVisible]="showGenerateModal()"
        (modalClose)="showGenerateModal.set(false)"
        (generate)="onLicenseGenerated($event)" />
}

<!-- Generate License Success Modal -->
@if (showGenerateSuccessModal()) {
    <app-confirmation-modal
        [isVisible]="showGenerateSuccessModal()"
        size="small"
        type="success"
        [loopAnimation]="false"
        [title]="'License Generated'"
        [message]="'Your license has been successfully generated.'"
        [confirmText]="'Close'"
        (confirm)="onGenerateSuccessModalClose()"
        (modalClose)="onGenerateSuccessModalClose()" />
}

<!-- Single License Delete Confirmation Modal -->
@if (showSingleDeleteModal()) {
    <app-confirmation-modal
        [isVisible]="showSingleDeleteModal()"
        [showCancelButton]="true"
        size="small"
        type="warning"
        [title]="'Delete License'"
        [message]="
            'Are you sure you want to delete license ' +
            (licenseToDelete()?.licenseKey ?? '') +
            '? This action cannot be undone.'
        "
        confirmText="Delete"
        cancelText="Cancel"
        (confirm)="onConfirmSingleDelete()"
        (cancel)="closeSingleDeleteModal()"
        (modalClose)="closeSingleDeleteModal()" />
}

<!-- Bulk Delete Confirmation Modal -->
@if (showDeleteModal()) {
    <app-confirmation-modal
        [isVisible]="showDeleteModal()"
        [showCancelButton]="true"
        size="small"
        [type]="'warning'"
        [title]="'Confirm Bulk Deleting Licenses'"
        [message]="
            'Are you sure you want to delete ' +
            selectedRows().length +
            ' licenses? This action cannot be undone.'
        "
        [confirmText]="'Delete'"
        cancelText="Cancel"
        (confirm)="onConfirmBulkDelete()"
        (cancel)="closeDeleteModal()"
        (modalClose)="closeDeleteModal()" />
}

<!-- Single Deletion Success Modal -->
@if (showSingleDeleteSuccessModal()) {
    <app-confirmation-modal
        [isVisible]="showSingleDeleteSuccessModal()"
        size="small"
        type="success"
        [loopAnimation]="false"
        [title]="'License Deleted'"
        [message]="'The license has been successfully deleted.'"
        [confirmText]="'Close'"
        (confirm)="showSingleDeleteSuccessModal.set(false)"
        (modalClose)="showSingleDeleteSuccessModal.set(false)" />
}

<!-- Bulk Deletion Success Modal -->
@if (showSuccessModal()) {
    <app-confirmation-modal
        [isVisible]="showSuccessModal()"
        size="small"
        type="success"
        [loopAnimation]="false"
        [title]="'Bulk Deletion Success'"
        [message]="'Selected licenses have been successfully deleted'"
        [confirmText]="'Close'"
        (confirm)="showSuccessModal.set(false)"
        (modalClose)="showSuccessModal.set(false)" />
}
